// ./src/components/Ad.tsx

import { useEffect, useState, useCallback } from 'react'
import styled from 'styled-components'
import { useLogFeatureInfo } from '../utils/session'
import { useUser } from '../data/user'

const GOOGLE_TAG_MANAGER_EVENT = 'ad_ready'
const AUTO_ROTATE_INTERVAL_MS = 5000

interface AdItem {
	image: string
	destination: string
}

// Styled Components
const AdContainer = styled.div`
	border-radius: 7px;
	overflow: hidden;
	width: calc(100% - 4px);
	margin-bottom: 20px;
	position: relative;

	& img {
		float: left;
		width: 100%;
		object-fit: cover;
	}
`

const AdSliderWrapper = styled.div`
	border-radius: 7px;
	overflow: hidden;
`

const AdSlider = styled.div<{ current: number }>`
	display: flex;
	transition: transform 0.4s ease-in-out;
	transform: translateX(${props => -props.current * 100}%);
`

const AdSlide = styled.div`
	flex: 0 0 100%;

	& img {
		float: left;
		width: 100%;
		object-fit: cover;
	}
`

const DotContainer = styled.div`
	display: flex;
	justify-content: center;
	gap: 8px;
	padding: 10px 0 4px 0;
`

const Dot = styled.div<{ active: boolean }>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: ${props => props.active ? '#1976d2' : '#ccc'};
	cursor: pointer;
	transition: background-color 0.2s ease;

	&:hover {
		background-color: ${props => props.active ? '#1976d2' : '#999'};
	}
`

/**
 * Reads ads from the DOM element injected by Google Tag Manager.
 * Supports both new array format (data-ads) and legacy single ad format (data-image/data-destination).
 */
const read_ads_from_dom = (): AdItem[] => {
	const ad_el = document.getElementById('ad-image-data')
	if (!ad_el) return []

	// Try new array format first (data-ads contains JSON array)
	const ads_json = ad_el.getAttribute('data-ads')
	if (ads_json) {
		try {
			const parsed = JSON.parse(ads_json)
			if (Array.isArray(parsed)) {
				return parsed
					.filter((ad: any) => ad.image_url || ad.image)
					.map((ad: any) => ({
						image: ad.image_url || ad.image,
						destination: ad.destination_url || ad.destination || ''
					}))
			}
		} catch (e) {
			console.warn('[Ad] Failed to parse ads JSON:', e)
		}
	}

	// Fallback to legacy single ad format
	const image = ad_el.getAttribute('data-image')
	const destination = ad_el.getAttribute('data-destination')
	if (image) {
		return [{ image, destination: destination || '' }]
	}

	return []
}

/**
 * Compares two ad arrays for equality
 */
const ads_are_equal = (a: AdItem[], b: AdItem[]): boolean => {
	if (a.length !== b.length) return false
	return a.every((ad, i) => ad.image === b[i].image && ad.destination === b[i].destination)
}

const Ad = () => {
	const [ads, set_ads] = useState<AdItem[]>([])
	const [current_index, set_current_index] = useState(0)
	const [is_paused, set_is_paused] = useState(false)

	// Click tracking
	const log_feat = useLogFeatureInfo()
	const user_op = useUser()

	const track_click = () => {
		try {
			const { id, email, first_name, last_name } = (user_op?.data?.users?.data||[])[0]||{}
			log_feat({
				title: 'ad_clicked_now_page',
				content: id ? { id, email, first_name, last_name } : {}
			})
		} catch(err) {
			console.warn(err)
		}
	}

	// Function to load ads from DOM
	const load_ads = useCallback(() => {
		const new_ads = read_ads_from_dom()
		set_ads(prev => {
			if (ads_are_equal(prev, new_ads)) return prev
			return new_ads
		})
	}, [])

	// Initial load and event listener for GTM updates
	useEffect(() => {
		load_ads()
		document.addEventListener(GOOGLE_TAG_MANAGER_EVENT, load_ads)
		return () => {
			document.removeEventListener(GOOGLE_TAG_MANAGER_EVENT, load_ads)
		}
	}, [load_ads])

	// Auto-rotate carousel (only if multiple ads and not paused)
	useEffect(() => {
		if (ads.length <= 1 || is_paused) return

		const timer = setInterval(() => {
			set_current_index(i => (i + 1) % ads.length)
		}, AUTO_ROTATE_INTERVAL_MS)

		return () => clearInterval(timer)
	}, [ads.length, is_paused])

	// Reset index if it's out of bounds after ads change
	useEffect(() => {
		if (current_index >= ads.length && ads.length > 0) {
			set_current_index(0)
		}
	}, [ads.length, current_index])

	// Handle dot click
	const handle_dot_click = (index: number) => {
		set_current_index(index)
	}

	// Pause on hover, resume on leave
	const handle_mouse_enter = () => set_is_paused(true)
	const handle_mouse_leave = () => set_is_paused(false)

	// Don't render if no ads
	if (ads.length === 0) {
		return <div></div>
	}

	// Single ad - render exactly like the old simple structure
	if (ads.length === 1) {
		const ad = ads[0]
		return (
			<AdContainer onClick={track_click}>
				<a href={ad.destination} target="_blank" rel="noreferrer">
					<img id="ad-banner" src={ad.image} alt="wand ad"/>
				</a>
			</AdContainer>
		)
	}

	// Multiple ads - render carousel
	return (
		<AdContainer
			onMouseEnter={handle_mouse_enter}
			onMouseLeave={handle_mouse_leave}
		>
			<AdSliderWrapper>
				<AdSlider current={current_index}>
					{ads.map((ad, i) => (
						<AdSlide key={i}>
							<a href={ad.destination} target="_blank" rel="noreferrer" onClick={track_click}>
								<img
									id={i === 0 ? 'ad-banner' : undefined}
									src={ad.image}
									alt="wand ad"
								/>
							</a>
						</AdSlide>
					))}
				</AdSlider>
			</AdSliderWrapper>
			<DotContainer>
				{ads.map((_, i) => (
					<Dot
						key={i}
						active={i === current_index}
						onClick={() => handle_dot_click(i)}
					/>
				))}
			</DotContainer>
		</AdContainer>
	)
}

export default Ad
